import React, { useState, useCallback } from 'react';
import {
  Search,
  X,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CircleCheck as CheckCircle,
  Loader as Loader2,
  Clock,
  Mail,
  PackageCheck,
  Inbox,
  ChevronDown,
  ChevronUp,
  User,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import { useProcessingQueue } from '../../hooks/useProcessingQueue';
import { IssueTypeBadge, SourceBadge } from './IssueTypeBadge';
import { ResolveNoteOverlay } from './ResolveNoteOverlay';
import type { OrderIssue } from '../../types/orderIssues';

interface ProcessingQueueSectionProps {
  status: 'under_process' | 'processed' | 'resolved';
  onStatsChanged?: () => void;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type ViewMode = 'under_process' | 'processed' | 'resolved';

interface IssueCardProps {
  issue: OrderIssue;
  viewMode: ViewMode;
  onMarkProcessed?: (id: string) => void;
  onResolve?: (id: string) => void;
  processing?: boolean;
}

const IssueCard: React.FC<IssueCardProps> = ({ issue, viewMode, onMarkProcessed, onResolve, processing }) => {
  const [expanded, setExpanded] = useState(false);
  const addr = issue.recipient_address || {};
  const addressParts = [
    addr.street || addr.address1,
    addr.houseNumber,
    addr.postalCode,
    addr.city,
    addr.countryCode,
  ].filter(Boolean);

  const iconBg =
    viewMode === 'resolved'
      ? 'bg-emerald-100 dark:bg-emerald-900/30'
      : viewMode === 'processed'
        ? 'bg-violet-100 dark:bg-violet-900/30'
        : 'bg-blue-100 dark:bg-blue-900/30';

  const IconComponent =
    viewMode === 'resolved' ? CheckCircle : viewMode === 'processed' ? PackageCheck : Clock;

  const iconColor =
    viewMode === 'resolved'
      ? 'text-emerald-600 dark:text-emerald-400'
      : viewMode === 'processed'
        ? 'text-violet-600 dark:text-violet-400'
        : 'text-blue-600 dark:text-blue-400';

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 transition-all hover:shadow-md">
      <div className="flex items-start gap-4 p-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <IconComponent className={`h-5 w-5 ${iconColor}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
              {issue.order_reference}
            </span>
            <IssueTypeBadge type={issue.issue_type} />
            <SourceBadge source={issue.source} />
            {issue.attempt_count > 1 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                {issue.attempt_count}x
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            {issue.client_name && (
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {issue.client_name}
              </span>
            )}
            {issue.email_sent_at && (
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Emailed {formatShortDate(issue.email_sent_at)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Created {formatShortDate(issue.created_at)}
            </span>
          </div>

          {viewMode === 'processed' && issue.processed_at && (
            <div className="flex items-center gap-2 mt-2 px-2.5 py-1.5 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800/30 w-fit">
              <PackageCheck className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
              <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
                Processed {formatDate(issue.processed_at)}
              </span>
              {issue.processed_by_name && (
                <>
                  <span className="text-violet-300 dark:text-violet-600">|</span>
                  <User className="h-3 w-3 text-violet-600 dark:text-violet-400" />
                  <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
                    {issue.processed_by_name}
                  </span>
                </>
              )}
            </div>
          )}

          {viewMode === 'resolved' && issue.resolved_at && (
            <div className="flex items-center gap-2 mt-2 px-2.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30 w-fit">
              <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                Resolved {formatDate(issue.resolved_at)}
              </span>
              {issue.resolved_by_name && (
                <>
                  <span className="text-emerald-300 dark:text-emerald-600">|</span>
                  <User className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    {issue.resolved_by_name}
                  </span>
                </>
              )}
            </div>
          )}

          {viewMode === 'resolved' && issue.resolution_note && (
            <div className="flex items-start gap-2 mt-2 px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600/30 w-fit max-w-full">
              <FileText className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-gray-600 dark:text-gray-300 break-words">
                {issue.resolution_note}
              </span>
            </div>
          )}

          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
            {issue.issue_description}
          </p>

          {expanded && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Address
                  </span>
                  <p className="mt-1 text-gray-700 dark:text-gray-300 text-xs">
                    {addressParts.length > 0 ? addressParts.join(', ') : 'No address data'}
                  </p>
                </div>
                {issue.error_code && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Error Code
                    </span>
                    <p className="mt-1">
                      <code className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs font-mono">
                        {issue.error_code}
                      </code>
                    </p>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Full Error
                  </span>
                  <p className="mt-1 text-gray-600 dark:text-gray-400 text-xs leading-relaxed break-words">
                    {issue.issue_description}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400"
            title={expanded ? 'Collapse' : 'Expand details'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {viewMode === 'under_process' && (
            <>
              {onResolve && (
                <button
                  onClick={() => onResolve(issue.id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all"
                  title="Resolve"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Resolve
                </button>
              )}
              {onMarkProcessed && (
                <button
                  onClick={() => onMarkProcessed(issue.id)}
                  disabled={processing}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-sm shadow-violet-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Mark as Processed"
                >
                  {processing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <PackageCheck className="h-3.5 w-3.5" />
                  )}
                  Processed
                </button>
              )}
            </>
          )}

          {viewMode === 'processed' && onResolve && (
            <button
              onClick={() => onResolve(issue.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-sm shadow-emerald-500/20 transition-all"
              title="Resolve"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Resolve
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const VIEW_CONFIG = {
  under_process: {
    title: 'Under Process',
    subtitle: 'Orders sent by email awaiting processing',
    emptyTitle: 'No orders under process',
    emptySubtitle: 'When issue emails are sent, orders will appear here for review. Mark them as processed once handled.',
    headerGradient: 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/20',
    headerIcon: Clock,
    badgeBg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    emptyIconBg: 'bg-blue-100 dark:bg-blue-900/30',
    emptyIcon: Inbox,
    emptyIconColor: 'text-blue-500',
  },
  processed: {
    title: 'Processed',
    subtitle: 'Orders that have been reviewed and marked as processed',
    emptyTitle: 'No processed orders',
    emptySubtitle: 'Orders you mark as processed will appear here as a completed archive.',
    headerGradient: 'bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/20',
    headerIcon: PackageCheck,
    badgeBg: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    emptyIconBg: 'bg-violet-100 dark:bg-violet-900/30',
    emptyIcon: PackageCheck,
    emptyIconColor: 'text-violet-500',
  },
  resolved: {
    title: 'Resolved',
    subtitle: 'Issues that have been resolved and closed',
    emptyTitle: 'No resolved issues',
    emptySubtitle: 'When you resolve issues from Under Process or Processed, they will appear here.',
    headerGradient: 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/20',
    headerIcon: CheckCircle,
    badgeBg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    emptyIconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    emptyIcon: CheckCircle,
    emptyIconColor: 'text-emerald-500',
  },
} as const;

export const ProcessingQueueSection: React.FC<ProcessingQueueSectionProps> = ({
  status,
  onStatsChanged,
}) => {
  const {
    issues,
    loading,
    total,
    page,
    pageSize,
    search,
    sortDirection,
    setPage,
    updateSearch,
    toggleSort,
    loadIssues,
    markAsProcessed,
    resolveIssues,
  } = useProcessingQueue(status);

  const [refreshing, setRefreshing] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [resolveTarget, setResolveTarget] = useState<OrderIssue | null>(null);

  const config = VIEW_CONFIG[status];
  const HeaderIcon = config.headerIcon;
  const EmptyIcon = config.emptyIcon;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadIssues();
    setRefreshing(false);
  }, [loadIssues]);

  const handleMarkProcessed = useCallback(
    async (issueId: string) => {
      setProcessingIds((prev) => new Set(prev).add(issueId));
      await markAsProcessed([issueId]);
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(issueId);
        return next;
      });
      onStatsChanged?.();
    },
    [markAsProcessed, onStatsChanged]
  );

  const handleResolveClick = useCallback(
    (issueId: string) => {
      const issue = issues.find((i) => i.id === issueId);
      if (issue) setResolveTarget(issue);
    },
    [issues]
  );

  const handleResolveConfirm = useCallback(
    async (note?: string) => {
      if (!resolveTarget) return;
      await resolveIssues([resolveTarget.id], note);
      setResolveTarget(null);
      onStatsChanged?.();
    },
    [resolveTarget, resolveIssues, onStatsChanged]
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-5 border-b border-gray-100 dark:border-gray-700/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.headerGradient}`}>
              <HeaderIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {config.title}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {config.subtitle}
              </p>
            </div>
            {!loading && (
              <span className={`ml-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${config.badgeBg}`}>
                {total}
              </span>
            )}
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 text-gray-600 dark:text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order number or customer name..."
              value={search}
              onChange={(e) => updateSearch(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
            />
            {search && (
              <button
                onClick={() => updateSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <button
            onClick={toggleSort}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
          >
            {sortDirection === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUp className="h-4 w-4" />
            )}
            {sortDirection === 'desc' ? 'Newest First' : 'Oldest First'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
        </div>
      ) : issues.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${config.emptyIconBg}`}>
            <EmptyIcon className={`h-8 w-8 ${config.emptyIconColor}`} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {config.emptyTitle}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
            {config.emptySubtitle}
          </p>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {issues.map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              viewMode={status}
              onMarkProcessed={status === 'under_process' ? handleMarkProcessed : undefined}
              onResolve={status !== 'resolved' ? handleResolveClick : undefined}
              processing={processingIds.has(issue.id)}
            />
          ))}
        </div>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 dark:border-gray-700/50">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {resolveTarget && (
        <ResolveNoteOverlay
          orderReference={resolveTarget.order_reference}
          onConfirm={handleResolveConfirm}
          onClose={() => setResolveTarget(null)}
        />
      )}
    </div>
  );
};
